import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import InfoTooltip from "./InfoTooltip";
import { getDisplayedPrice } from "./priceUtils";

const PlanCard = ({ isYearly, tier, tierIndex }) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth).userInfo;
  const display = getDisplayedPrice(tier, userInfo);

  return (
    <div key={`${tier.name}-${tierIndex}`} className="relative">
      {tier.subheader && (
        <p className="absolute top-0 left-1/2 py-1.5 z-10 px-4 uppercase text-xs font-semibold bg-blue-50 text-white rounded-full tracking-wide transform -translate-x-1/2 -translate-y-1/2">
          {tier.subheader}
        </p>
      )}
      <div className="relative p-6 md:p-8 border border-gray-200 rounded-2xl overflow-hidden shadow-lg flex flex-col bg-white text-black">
        {tier.trial_Label && (
          <div class="absolute right-0 top-0 h-16 w-16">
            <div class="absolute text-xs transform rotate-45 bg-blue-100 text-center text-white font-semibold py-1 right-[-35px] top-[32px] w-[170px]">
              Trial Started
            </div>
          </div>
        )}
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-center">{tier.title}</h2>

          <p className="mt-4 flex flex-col items-center justify-center">
            {display.isPromo && (
              <p className="text-sm text-red-500 font-semibold mb-1">
                🎉 Promo -{display.percent}% limitée !
              </p>
            )}
            <div className="flex items-baseline justify-center">
              {display.isPromo && (
                <span className="text-xl font-semibold text-gray-400 line-through mr-2">
                  {display.oldPrice}€
                </span>
              )}
              <span className="text-6xl font-bold text-blue-100">
                {display.price}
              </span>
              <span className="text-4xl font-bold text-blue-100">€</span>
              {tier.type === "subscription" && (
                <span className="text-xl font-semibold ml-1">
                  {/* /{isYearly === true ? t("Year", "an") : t("month", "mois")} */}
                  /{t("month", "mois")}
                </span>
              )}
            </div>
          </p>

          <p className="text-center text-sm mt-4">{tier.description}</p>

          {/* Prix par contenu */}
          <div className="text-center text-sm mt-2">
            <span className="text-black/50">
              Soit{" "}
              {display.isPromo && (
                <>
                  <span className="text-gray-400 line-through">
                    {display.oldPricePerContent}€
                  </span>{" "}
                </>
              )}
              <span className="text-blue-100 font-semibold">
                {display.pricePerContent}€
              </span>{" "}
              par contenu
            </span>
          </div>

          <div className="flex flex-col items-center">
            {/* Button */}
            <button
              onClick={tier.onClick}
              className={`mt-6 block w-full py-3 px-6 rounded-md text-center font-semibold border-2 border-solid transition transform ${
                tier.disabled
                  ? "bg-gray_local-400 text-white border-gray_local-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-100 to-blue-50 text-white border-transparent hover:from-blue-50 hover:to-blue-100 hover:scale-105"
              }`}
              disabled={tier.disabled}
            >
              {!tier.loading ? (
                tier?.buttonText
              ) : (
                <ClipLoader size={25} color="white" />
              )}
            </button>

            {/* Secure Payment Text */}
            <p className="mt-2 text-center text-sm text-gray-500">
              🔒 Paiements sécurisés par <strong>Stripe</strong>
            </p>
          </div>

          <p className="text-center text-sm mt-6 font-bold">
            Ce plan comprend :
          </p>
          <ul role="list" className="mt-4 space-y-3">
            {tier.features_list.map((feature, lineIndex) => {
              const featureText =
                typeof feature === "string" ? feature : feature.text;
              const featureTooltip =
                typeof feature === "object" ? feature.tooltip : null;

              return (
                <li
                  key={`${tier.name}-line-${lineIndex}`}
                  className="flex items-start text-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5 mr-2 text-blue-100 flex-shrink-0"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      fill="none"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4"
                    />
                  </svg>

                  {featureTooltip ? (
                    <InfoTooltip content={featureTooltip}>
                      <span>{featureText}</span>
                    </InfoTooltip>
                  ) : (
                    <span>{featureText}</span>
                  )}
                </li>
              );
            })}
            {tier.features_missing &&
              tier.features_missing.map((feature, lineIndex) => {
                const featureText =
                  typeof feature === "string" ? feature : feature.text;
                const featureTooltip =
                  typeof feature === "object" ? feature.tooltip : null;

                return (
                  <li
                    key={`${tier.name}-line-${lineIndex}`}
                    className="flex items-start text-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-5 h-5 mr-2 text-red-500 flex-shrink-0"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        fill="none"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 9l-6 6m0-6l6 6"
                      />
                    </svg>

                    {featureTooltip ? (
                      <InfoTooltip content={featureTooltip}>
                        <span>{featureText}</span>
                      </InfoTooltip>
                    ) : (
                      <span>{featureText}</span>
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;
